import { z } from "zod";
import { companyTypes, invoiceCurrencies, invoiceStatuses, languages, lineItemTypes, awsResourceTypes, projectGroupStatuses, projectStatuses, projectTypes, productionModes, quoteVersionStatuses, taskStates, vatRates, } from "./projects-schema.js";
import { CONNECTION_TYPES, DEVICE_KINDS } from "../types/connections.js";
import { LINK_VERIFIED_BY, PATCH_STATUSES, PORT_DIRECTIONS, } from "../types/wiring.js";
import { isBridgeUrl } from "../types/bridge.js";
import { VMIX_OUTPUTS } from "../types/vmix-outputs.js";
const uuidSchema = z.string().uuid();
const nullableUUIDSchema = uuidSchema.nullable().optional();
const boundedNullableTextSchema = (max) => z.string().trim().max(max).nullable().optional();
const nullableDateSchema = z.string().date().nullable().optional();
const jsonValueSchema = z.any();
const projectFields = {
    number: boundedNullableTextSchema(50),
    name: z.string().trim().min(1).max(500),
    customerID: nullableUUIDSchema,
    contactID: nullableUUIDSchema,
    projectGroupID: nullableUUIDSchema,
    studioID: nullableUUIDSchema,
    accountManagerID: nullableUUIDSchema,
    projectManagerID: nullableUUIDSchema,
    technicalResponsibleID: nullableUUIDSchema,
    equipmentResponsibleID: nullableUUIDSchema,
    status: z.enum(projectStatuses).nullable().optional(),
    types: z.array(z.enum(projectTypes)).nullable().optional(),
    startDate: nullableDateSchema,
    endDate: nullableDateSchema,
    eventStartDate: nullableDateSchema,
    eventEndDate: nullableDateSchema,
    planningStartDate: nullableDateSchema,
    planningEndDate: nullableDateSchema,
    notes: boundedNullableTextSchema(20000),
    conditions: boundedNullableTextSchema(20000),
    language: z.enum(languages).optional(),
    defaultVatRate: z.enum(vatRates).optional(),
    defaultDiscountPercent: z.number().min(0).max(100).optional(),
    crewListRemarks: boundedNullableTextSchema(10000),
    mealsRemarks: boundedNullableTextSchema(10000),
    hotelsRemarks: boundedNullableTextSchema(10000),
    productionLocation: boundedNullableTextSchema(1000),
    productionMode: z.enum(productionModes).nullable().optional(),
    rentmanID: z.number().int().positive().nullable().optional(),
};
export const createProjectInputSchema = z.object({
    id: uuidSchema,
    ...projectFields,
});
export const updateProjectInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(projectFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one project field must be updated");
export const projectIDInputSchema = z.object({
    id: uuidSchema,
});
const companyFields = {
    number: boundedNullableTextSchema(50),
    name: z.string().trim().min(1).max(255),
    invoiceAddress: boundedNullableTextSchema(1000),
    language: boundedNullableTextSchema(10),
    types: z.array(z.enum(companyTypes)).nullable().optional(),
    isMainCompany: z.boolean().nullable().optional(),
    rentmanID: z.number().int().positive().nullable().optional(),
};
export const createCompanyInputSchema = z.object({
    id: uuidSchema,
    ...companyFields,
});
export const updateCompanyInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(companyFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one company field must be updated");
export const companyIDInputSchema = z.object({
    id: uuidSchema,
});
const projectGroupFields = {
    name: z.string().trim().min(1).max(255),
    number: boundedNullableTextSchema(50),
    customerID: nullableUUIDSchema,
    contactID: nullableUUIDSchema,
    description: boundedNullableTextSchema(5000),
    status: z.enum(projectGroupStatuses).optional(),
    color: boundedNullableTextSchema(100),
};
export const createProjectGroupInputSchema = z.object({
    id: uuidSchema,
    ...projectGroupFields,
});
export const updateProjectGroupInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(projectGroupFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one project group field must be updated");
export const projectGroupIDInputSchema = z.object({
    id: uuidSchema,
});
const personTypes = ["contact", "employee", "freelancer"];
const personFields = {
    companyID: nullableUUIDSchema,
    firstName: z.string().trim().min(1).max(255),
    lastName: z.string().trim().min(1).max(255),
    number: boundedNullableTextSchema(50),
    function: boundedNullableTextSchema(255),
    phone: boundedNullableTextSchema(100),
    email: boundedNullableTextSchema(255),
    personType: z.enum(personTypes).optional(),
    isAdmin: z.boolean().optional(),
    clockifyUserID: boundedNullableTextSchema(255),
    rentmanID: z.number().int().positive().nullable().optional(),
    allergies: boundedNullableTextSchema(5000),
    remarks: boundedNullableTextSchema(5000),
    notes: boundedNullableTextSchema(20000),
    allergiesNotes: boundedNullableTextSchema(5000),
    unityUsername: boundedNullableTextSchema(255),
};
export const createPersonInputSchema = z.object({
    id: uuidSchema,
    ...personFields,
});
export const updatePersonInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(personFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one person field must be updated");
export const personIDInputSchema = z.object({
    id: uuidSchema,
});
export const listPeopleQueryArgsSchema = z.object({
    personTypes: z
        .array(z.enum(personTypes))
        .optional()
        .describe("Only return people of these types"),
    includeDeleted: z
        .boolean()
        .optional()
        .describe("Include soft-deleted people"),
});
export const personByUserIDQueryArgsSchema = z.object({
    userID: z
        .string()
        .min(1)
        .describe("Auth user ID; only resolves for the calling user"),
});
export const createPublicHolidayInputSchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(255),
    date: nullableDateSchema,
});
export const updatePublicHolidayInputSchema = z
    .object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(255).optional(),
    date: nullableDateSchema,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one public holiday field must be updated");
export const publicHolidayIDInputSchema = z.object({ id: uuidSchema });
const employeeAbsenceFields = {
    personID: uuidSchema,
    absenceType: z.enum(["vacation", "sick", "unpaid", "other"]),
    startDate: nullableDateSchema,
    endDate: nullableDateSchema,
    notes: boundedNullableTextSchema(5000),
};
export const createEmployeeAbsenceInputSchema = z.object({
    id: uuidSchema,
    ...employeeAbsenceFields,
});
export const updateEmployeeAbsenceInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(employeeAbsenceFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one employee absence field must be updated");
export const employeeAbsenceIDInputSchema = z.object({ id: uuidSchema });
export const upsertEmployeeEntitlementInputSchema = z.object({
    id: uuidSchema.optional(),
    personID: uuidSchema,
    year: z.number().int().min(1900).max(3000),
    vacationDays: z.number().min(0).optional(),
    carryOverDays: z.number().optional(),
    notes: boundedNullableTextSchema(5000),
});
const employmentPeriodFields = {
    personID: uuidSchema,
    percentage: z.number().min(0).max(100),
    validFrom: nullableDateSchema,
    validTo: nullableDateSchema,
};
export const createEmploymentPeriodInputSchema = z.object({
    id: uuidSchema,
    ...employmentPeriodFields,
});
export const updateEmploymentPeriodInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(employmentPeriodFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one employment period field must be updated");
export const employmentPeriodIDInputSchema = z.object({ id: uuidSchema });
export const createOvertimePayoutInputSchema = z.object({
    id: uuidSchema,
    personID: uuidSchema,
    date: nullableDateSchema,
    hours: z.number(),
    notes: boundedNullableTextSchema(5000),
    createdBy: nullableUUIDSchema,
});
export const overtimePayoutIDInputSchema = z.object({ id: uuidSchema });
export const updateAppSettingInputSchema = z.object({
    key: z.string().trim().min(1).max(255),
    value: z.any(),
});
export const createAllergyTagInputSchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(255),
});
export const setPersonAllergyTagsInputSchema = z.object({
    personID: uuidSchema,
    tagIDs: z.array(uuidSchema),
});
const studioFields = {
    number: boundedNullableTextSchema(50),
    name: z.string().trim().min(1).max(255),
    description: boundedNullableTextSchema(5000),
    // Admin-only (the studios mutators check it): the pairing key is sent there
    bridgeURL: z
        .string()
        .trim()
        .max(1000)
        .refine((value) => value === "" || isBridgeUrl(value), {
        message: "Must be an http(s) URL",
    })
        .nullable()
        .optional(),
    location: boundedNullableTextSchema(1000),
    unityHostname: boundedNullableTextSchema(500),
    // No activeConfigID: only the bridge's load report sets the active config
};
export const createStudioInputSchema = z.object({
    id: uuidSchema,
    ...studioFields,
});
export const updateStudioInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(studioFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one studio field must be updated");
export const studioIDInputSchema = z.object({ id: uuidSchema });
const studioConfigName = z.string().trim().min(1).max(255);
/**
 * New configs are always copies (of a template or any config): there is no
 * empty config, it would wipe all labels and routes on a Full load.
 */
export const copyStudioConfigInputSchema = z.object({
    id: uuidSchema,
    sourceConfigID: uuidSchema,
    /** Copy this version instead of the latest */
    sourceVersion: z.number().int().positive().optional(),
    /** Target studio (the source's, or another one) */
    studioID: uuidSchema,
    projectID: nullableUUIDSchema,
    kind: z.enum(["config", "template"]),
    name: studioConfigName,
    description: boundedNullableTextSchema(5000),
    /** Copies to another studio: source device ID → target device ID or null */
    deviceMap: z.record(z.string(), uuidSchema.nullable()).optional(),
});
export const updateStudioConfigInputSchema = z
    .object({
    id: uuidSchema,
    studioID: uuidSchema,
    name: studioConfigName.optional(),
    description: boundedNullableTextSchema(5000),
    projectID: nullableUUIDSchema,
    configData: jsonValueSchema.optional(),
})
    .refine(({ id: _id, studioID: _studioID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one studio config field must be updated");
const lmIoServerEntrySchema = z.object({
    id: z.string().min(1).max(100),
    name: z.string().trim().max(200),
    port_id: z.string().max(100).nullable(),
    notes: boundedNullableTextSchema(2000),
});
/** The whole lm-io-server setup of a config (cloud-owned, never locked). */
export const setStudioConfigLmIoServerInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
    lmIoServer: z.object({
        device_id: z.string().max(100).nullable(),
        sources: z.array(lmIoServerEntrySchema).max(200),
        outputs: z.array(lmIoServerEntrySchema).max(200),
    }),
});
export const setStudioConfigVmixOutputInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
    /** The DeckLink port */
    portID: uuidSchema,
    /** null: the port shows no vMix output */
    output: z.enum(VMIX_OUTPUTS).nullable(),
});
export const deleteStudioConfigInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
});
export const restoreStudioConfigVersionInputSchema = z.object({
    configID: uuidSchema,
    versionID: uuidSchema,
    studioID: uuidSchema,
});
export const setDefaultStudioTemplateInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
});
export const keepStudioScratchInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
    kind: z.enum(["config", "template"]),
    name: studioConfigName,
    projectID: nullableUUIDSchema,
});
export const saveStudioScratchToOriginalInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
});
export const resolveStudioConfigConflictInputSchema = z.object({
    configID: uuidSchema,
    versionID: uuidSchema,
    studioID: uuidSchema,
    /** "conflict": the conflict version becomes current; "current": dismiss it */
    keep: z.enum(["conflict", "current"]),
});
export const forceUnlockStudioConfigInputSchema = z.object({
    studioID: uuidSchema,
});
const studioConnectionFields = {
    studioID: uuidSchema,
    name: z.string().trim().min(1).max(255),
    connectionType: z.enum(CONNECTION_TYPES),
    host: boundedNullableTextSchema(255),
    port: z.number().int().positive().nullable().optional(),
    model: boundedNullableTextSchema(255),
    managementURL: z.string().trim().url().nullable().optional(),
    enabled: z.boolean().optional(),
    kind: z.enum(DEVICE_KINDS).optional(),
    parentID: nullableUUIDSchema,
    templateID: boundedNullableTextSchema(100),
    position: z.number().int().min(0).nullable().optional(),
    notes: boundedNullableTextSchema(10000),
    /** Its name on the Dante network, to match Dante routes to the device */
    danteName: boundedNullableTextSchema(255),
    /** SDI audio channels it originates ("1-4"), overriding its template */
    embeddedAudio: z
        .string()
        .trim()
        .max(100)
        .regex(/^(\d+(-\d+)?(\s*,\s*\d+(-\d+)?)*)?$/, 'Channels like "1-2" or "1-4, 7-8"')
        .nullable()
        .optional(),
    /** Driver-specific settings, e.g. device logins (see ConnectionOptions) */
    options: z
        .record(z.string(), z.union([z.string().max(1000), z.number(), z.boolean(), z.null()]))
        .refine((value) => Object.keys(value).length <= 50, "Too many options")
        .nullable()
        .optional(),
};
export const createStudioConnectionInputSchema = z.object({
    id: uuidSchema,
    ...studioConnectionFields,
    /**
     * Temporary gear: the config that owns the device (a scratch copy's
     * gear goes onto its original). In effect only while that config is.
     */
    configID: nullableUUIDSchema,
});
export const updateStudioConnectionInputSchema = z
    .object({
    id: uuidSchema,
    studioID: uuidSchema,
    name: studioConnectionFields.name.optional(),
    connectionType: studioConnectionFields.connectionType.optional(),
    host: studioConnectionFields.host,
    port: studioConnectionFields.port,
    model: studioConnectionFields.model,
    managementURL: studioConnectionFields.managementURL,
    enabled: studioConnectionFields.enabled,
    kind: studioConnectionFields.kind,
    parentID: studioConnectionFields.parentID,
    templateID: studioConnectionFields.templateID,
    position: studioConnectionFields.position,
    notes: studioConnectionFields.notes,
    danteName: studioConnectionFields.danteName,
    embeddedAudio: studioConnectionFields.embeddedAudio,
    options: studioConnectionFields.options,
})
    .refine(({ id: _id, studioID: _studioID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one studio connection field must be updated");
export const deleteStudioConnectionInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
});
export const moveStudioConnectionInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
    targetStudioID: uuidSchema,
});
const portControlSchema = z.union([
    z.object({
        kind: z.enum(["videohub_input", "videohub_output"]),
        index: z.number().int().min(0),
    }),
    z.object({
        kind: z.literal("atem_input"),
        input_id: z.number().int().min(0),
    }),
    z.object({
        kind: z.enum(["atem_aux", "atem_multiviewer"]),
        index: z.number().int().min(0),
    }),
]);
const studioPortFields = {
    direction: z.enum(PORT_DIRECTIONS),
    signal: z.string().trim().min(1).max(50),
    number: z.number().int().min(1).max(100000),
    name: boundedNullableTextSchema(255),
    group: boundedNullableTextSchema(100),
    control: portControlSchema.nullable().optional(),
};
export const createStudioPortInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
    deviceID: uuidSchema,
    ...studioPortFields,
});
export const updateStudioPortInputSchema = z
    .object({
    id: uuidSchema,
    studioID: uuidSchema,
    direction: studioPortFields.direction.optional(),
    signal: studioPortFields.signal.optional(),
    number: studioPortFields.number.optional(),
    name: studioPortFields.name,
    group: studioPortFields.group,
    control: studioPortFields.control,
})
    .refine(({ id: _id, studioID: _studioID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one port field must be updated");
export const deleteStudioPortInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
});
export const createStudioPortsFromTemplateInputSchema = z.object({
    studioID: uuidSchema,
    deviceID: uuidSchema,
    /** Device template ID; defaults to the device's own template */
    templateID: boundedNullableTextSchema(100),
    /** Instead of a template: N inputs / M outputs of one signal */
    generic: z
        .object({
        inputs: z.number().int().min(0).max(512),
        outputs: z.number().int().min(0).max(512),
        bidirectional: z.number().int().min(0).max(512).optional(),
        signal: z.string().trim().min(1).max(50),
    })
        .nullable()
        .optional(),
});
export const addStudioPortsInputSchema = z.object({
    studioID: uuidSchema,
    deviceID: uuidSchema,
    ports: z
        .array(z.object({
        id: uuidSchema.optional(),
        ...studioPortFields,
    }))
        .min(1)
        .max(1024),
});
const studioLinkFields = {
    fromPortID: uuidSchema,
    toPortID: uuidSchema,
    cable: boundedNullableTextSchema(255),
    patchPoint: boundedNullableTextSchema(255),
    notes: boundedNullableTextSchema(10000),
    /** Epoch milliseconds */
    verifiedAt: z.number().int().nonnegative().nullable().optional(),
    verifiedBy: z.enum(LINK_VERIFIED_BY).nullable().optional(),
};
export const createStudioLinkInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
    ...studioLinkFields,
    /**
     * A re-patch (or the cable of temporary gear): the config that owns it.
     * While it is in effect it suspends the permanent links on its ports.
     */
    configID: nullableUUIDSchema,
});
export const setStudioLinkPatchStatusInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
    patchStatus: z.enum(PATCH_STATUSES),
});
export const removeStudioConfigGearInputSchema = z.object({
    configID: uuidSchema,
    studioID: uuidSchema,
});
export const updateStudioLinkInputSchema = z
    .object({
    id: uuidSchema,
    studioID: uuidSchema,
    fromPortID: studioLinkFields.fromPortID.optional(),
    toPortID: studioLinkFields.toPortID.optional(),
    cable: studioLinkFields.cable,
    patchPoint: studioLinkFields.patchPoint,
    notes: studioLinkFields.notes,
    verifiedAt: studioLinkFields.verifiedAt,
    verifiedBy: studioLinkFields.verifiedBy,
})
    .refine(({ id: _id, studioID: _studioID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one link field must be updated");
export const deleteStudioLinkInputSchema = z.object({
    id: uuidSchema,
    studioID: uuidSchema,
});
const projectAwsResourceLinkFields = {
    projectID: uuidSchema,
    resourceType: z.enum(awsResourceTypes),
    resourceID: boundedNullableTextSchema(500),
    resourceArn: boundedNullableTextSchema(1000),
    resourceName: boundedNullableTextSchema(500),
    metadata: jsonValueSchema.optional(),
};
export const createProjectAwsResourceLinkInputSchema = z
    .object({
    id: uuidSchema,
    ...projectAwsResourceLinkFields,
})
    .refine(({ resourceID, resourceArn }) => Boolean(resourceID || resourceArn), "Resource ID or ARN is required");
export const updateProjectAwsResourceLinkInputSchema = z
    .object({
    id: uuidSchema,
    projectID: uuidSchema,
    resourceID: projectAwsResourceLinkFields.resourceID,
    resourceArn: projectAwsResourceLinkFields.resourceArn,
    resourceName: projectAwsResourceLinkFields.resourceName,
    metadata: jsonValueSchema.optional(),
})
    .refine(({ id: _id, projectID: _projectID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one project AWS resource link field must be updated");
export const deleteProjectAwsResourceLinkInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
// ============ Stream Audio Feeds ============
export const createStreamAudioFeedInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(255),
    sortOrder: z.number().int().optional(),
});
export const deleteStreamAudioFeedInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const updateStreamAudioFeedInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(255).optional(),
    sortOrder: z.number().int().min(0).optional(),
});
// ============ Stream Video Feeds ============
export const createStreamVideoFeedInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(255),
    description: z.string().max(1000).optional(),
    sortOrder: z.number().int().optional(),
});
export const deleteStreamVideoFeedInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const updateStreamVideoFeedInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().max(1000).optional(),
});
// ============ Stream Signals ============
export const createStreamSignalInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    number: z.number().int().min(1),
    name: z.string().trim().max(255).optional(),
    description: z.string().max(1000).optional(),
    videoFeedID: nullableUUIDSchema,
    sortOrder: z.number().int().optional(),
});
export const deleteStreamSignalInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const updateStreamSignalInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: z.string().trim().max(255).optional(),
    description: z.string().max(1000).optional(),
    videoFeedID: nullableUUIDSchema.optional(),
    number: z.number().int().min(1).optional(),
    sortOrder: z.number().int().optional(),
});
// ============ Stream Signal Audio Feeds ============
export const createStreamSignalAudioFeedInputSchema = z.object({
    id: uuidSchema,
    signalID: uuidSchema,
    audioFeedID: uuidSchema,
    sortOrder: z.number().int().optional(),
});
export const updateStreamSignalAudioFeedInputSchema = z.object({
    id: uuidSchema,
    signalID: uuidSchema,
    sortOrder: z.number().int().min(0),
});
export const deleteStreamSignalAudioFeedInputSchema = z.object({
    id: uuidSchema,
    signalID: uuidSchema,
});
// ============ Stream Takers ============
const streamTakerFields = {
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(255),
    deliveryMethod: z.string().trim().max(255).optional(),
    url: z.string().trim().max(2000).optional(),
    signalID: nullableUUIDSchema,
    contact: z.string().trim().max(5000).optional(),
    notes: z.string().trim().max(5000).optional(),
    testDate: z.number().nullable().optional(),
    tested: z.boolean().optional(),
    preTestDone: z.boolean().optional(),
    onDayTestDone: z.boolean().optional(),
};
export const createStreamTakerInputSchema = z.object({
    id: uuidSchema,
    ...streamTakerFields,
});
export const updateStreamTakerInputSchema = z
    .object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: streamTakerFields.name.optional(),
    deliveryMethod: streamTakerFields.deliveryMethod,
    url: streamTakerFields.url,
    signalID: streamTakerFields.signalID,
    contact: streamTakerFields.contact,
    notes: streamTakerFields.notes,
    testDate: streamTakerFields.testDate,
    tested: streamTakerFields.tested,
    preTestDone: streamTakerFields.preTestDone,
    onDayTestDone: streamTakerFields.onDayTestDone,
})
    .refine(({ id: _id, projectID: _projectID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one stream taker field must be updated");
export const deleteStreamTakerInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
// ============ Stream Taker Audio Pairs ============
export const createStreamTakerAudioPairInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
    pairNumber: z.number().int().min(1),
    audioFeedID: nullableUUIDSchema,
});
export const updateStreamTakerAudioPairInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
    audioFeedID: nullableUUIDSchema,
});
export const deleteStreamTakerAudioPairInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
});
// ============ Stream Taker Contacts ============
export const createStreamTakerContactInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
    personID: uuidSchema,
    description: z.string().trim().max(255).optional(),
});
export const updateStreamTakerContactInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
    personID: uuidSchema.optional(),
    description: z.string().trim().max(255).optional(),
});
export const deleteStreamTakerContactInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
});
// ============ Stream Taker Audio Pair Tests ============
export const createStreamTakerAudioPairTestInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
    signalAudioFeedID: uuidSchema,
    tested: z.boolean().optional(),
    preTestDone: z.boolean().optional(),
    onDayTestDone: z.boolean().optional(),
});
export const updateStreamTakerAudioPairTestInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
    tested: z.boolean().optional(),
    preTestDone: z.boolean().optional(),
    onDayTestDone: z.boolean().optional(),
});
export const deleteStreamTakerAudioPairTestInputSchema = z.object({
    id: uuidSchema,
    streamTakerID: uuidSchema,
});
const defaultFunctionFields = {
    name: z.string().trim().min(1).max(255),
    defaultFunctionGroupID: nullableUUIDSchema,
    displayOrder: z.number().int().min(0).optional(),
    systemTag: boundedNullableTextSchema(255),
    locationType: z.enum(productionModes).nullable().optional(),
    showOnInitialize: z.boolean().optional(),
};
const defaultFunctionGroupFields = {
    name: z.string().trim().min(1).max(255),
    locationType: z.enum(productionModes).nullable().optional(),
    displayOrder: z.number().int().min(0).optional(),
};
export const createDefaultFunctionGroupInputSchema = z.object({
    id: uuidSchema,
    ...defaultFunctionGroupFields,
});
export const updateDefaultFunctionGroupInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(defaultFunctionGroupFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one default function group field must be updated");
export const defaultFunctionGroupIDInputSchema = z.object({
    id: uuidSchema,
});
export const createDefaultFunctionInputSchema = z.object({
    id: uuidSchema,
    ...defaultFunctionFields,
});
export const updateDefaultFunctionInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(defaultFunctionFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one default function field must be updated");
export const defaultFunctionIDInputSchema = z.object({
    id: uuidSchema,
});
export const upsertSkillRatingInputSchema = z.object({
    id: uuidSchema,
    personID: uuidSchema,
    defaultFunctionID: uuidSchema,
    rating: z.number().int().min(1).max(5),
    notes: boundedNullableTextSchema(5000),
});
export const deleteSkillRatingInputSchema = z.object({
    id: uuidSchema,
});
const availabilityStatuses = [
    "unknown",
    "requested",
    "not_available",
    "available",
    "booked",
];
const projectAvailabilityFields = {
    projectID: uuidSchema,
    personID: uuidSchema,
    status: z.enum(availabilityStatuses),
    availableDays: z.array(z.string().date()).optional(),
    notes: boundedNullableTextSchema(5000),
};
export const upsertProjectAvailabilityInputSchema = z.object({
    id: uuidSchema,
    ...projectAvailabilityFields,
});
export const batchUpsertProjectAvailabilityInputSchema = z.object({
    projectID: uuidSchema,
    personIDs: z.array(uuidSchema).min(1),
    status: z.enum(availabilityStatuses),
    availableDays: z.array(z.string().date()).optional(),
});
export const deleteProjectAvailabilityInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const updateProjectAvailabilityRemarkInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    crewListRemark: boundedNullableTextSchema(5000),
});
export const updateProjectAvailabilityUnityUsernameInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    unityUsername: boundedNullableTextSchema(255),
});
export const createProjectContactInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    personID: uuidSchema,
    description: z.string().trim().min(1).max(1000),
});
export const updateProjectContactInputSchema = z
    .object({
    id: uuidSchema,
    projectID: uuidSchema,
    personID: uuidSchema.optional(),
    description: z.string().trim().min(1).max(1000).optional(),
})
    .refine(({ id: _id, projectID: _projectID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one project contact field must be updated");
export const deleteProjectContactInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const createProjectCollaboratorInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    personID: uuidSchema,
    canEdit: z.boolean().optional(),
});
export const updateProjectCollaboratorInputSchema = z
    .object({
    id: uuidSchema,
    projectID: uuidSchema,
    canEdit: z.boolean().optional(),
})
    .refine(({ id: _id, projectID: _projectID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one project collaborator field must be updated");
export const deleteProjectCollaboratorInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const upsertProjectMealPlanInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    personID: uuidSchema,
    planDate: z.string().date(),
    lunch: z.boolean(),
    dinner: z.boolean(),
});
export const batchUpsertProjectMealPlansInputSchema = z.object({
    projectID: uuidSchema,
    plans: z.array(upsertProjectMealPlanInputSchema),
});
export const upsertProjectHotelPlanInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    personID: uuidSchema,
    planDate: z.string().date(),
    needsHotel: z.boolean(),
    roomType: z.enum(["single", "double"]).optional(),
    roommatePersonID: uuidSchema.nullable().optional(),
});
export const batchUpsertProjectHotelPlansInputSchema = z.object({
    projectID: uuidSchema,
    plans: z.array(upsertProjectHotelPlanInputSchema),
});
export const linkNetworkCoverageSessionInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    externalSessionID: z.string().trim().min(1).max(255),
    deviceName: boundedNullableTextSchema(255),
    deviceUID: boundedNullableTextSchema(255),
    startedAt: z.number(),
    endedAt: z.number().nullable().optional(),
    hasGpx: z.boolean().optional(),
    summaryStats: jsonValueSchema.optional(),
});
export const unlinkNetworkCoverageSessionInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
const projectFunctionSeriesFields = {
    projectID: uuidSchema,
    projectFunctionGroupID: nullableUUIDSchema,
    name: z.string().trim().min(1).max(255),
    requiredCount: z.number().int().min(0).optional(),
    selectedDays: z.array(z.string().date()).optional(),
    systemTag: boundedNullableTextSchema(255),
    locationType: z.enum(productionModes).nullable().optional(),
};
const projectFunctionGroupFields = {
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(255),
    locationType: z.enum(productionModes).nullable().optional(),
    displayOrder: z.number().int().min(0).optional(),
};
export const createProjectFunctionGroupInputSchema = z.object({
    id: uuidSchema,
    ...projectFunctionGroupFields,
});
export const updateProjectFunctionGroupInputSchema = z
    .object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: projectFunctionGroupFields.name.optional(),
    locationType: projectFunctionGroupFields.locationType,
    displayOrder: projectFunctionGroupFields.displayOrder,
})
    .refine(({ id: _id, projectID: _projectID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one project function group field must be updated");
export const deleteProjectFunctionGroupInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const createProjectFunctionSeriesInputSchema = z.object({
    id: uuidSchema,
    ...projectFunctionSeriesFields,
    dayIDsByDate: z.record(z.string(), uuidSchema).optional(),
});
export const updateProjectFunctionSeriesInputSchema = z
    .object({
    id: uuidSchema,
    projectID: uuidSchema,
    projectFunctionGroupID: projectFunctionSeriesFields.projectFunctionGroupID,
    name: projectFunctionSeriesFields.name.optional(),
    requiredCount: projectFunctionSeriesFields.requiredCount,
    selectedDays: projectFunctionSeriesFields.selectedDays,
    systemTag: projectFunctionSeriesFields.systemTag,
    locationType: projectFunctionSeriesFields.locationType,
})
    .refine(({ id: _id, projectID: _projectID, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one project function field must be updated");
export const deleteProjectFunctionSeriesInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const updateProjectFunctionDayInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    requiredCountOverride: z.number().int().min(0).nullable(),
});
export const createFunctionAssignmentInputSchema = z.object({
    id: uuidSchema,
    functionID: uuidSchema,
    personID: uuidSchema,
    projectID: uuidSchema,
    remark: boundedNullableTextSchema(5000),
});
export const bulkAssignFunctionSeriesInputSchema = z.object({
    projectID: uuidSchema,
    personID: uuidSchema,
    functionIDs: z.array(uuidSchema),
});
export const updateFunctionAssignmentInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    remark: boundedNullableTextSchema(5000),
});
export const deleteFunctionAssignmentInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
export const initializeProjectFunctionsInputSchema = z.object({
    projectID: uuidSchema,
    functions: z.array(z.object({
        id: uuidSchema,
        projectFunctionGroupID: nullableUUIDSchema,
        name: z.string().trim().min(1).max(255),
        requiredCount: z.number().int().min(0),
        selectedDays: z.array(z.string().date()),
        systemTag: boundedNullableTextSchema(255),
        locationType: z.enum(productionModes).nullable().optional(),
        dayOverrides: z.array(z.object({
            date: z.string().date(),
            count: z.number().int().min(0),
        })),
    })),
});
export const upsertProjectPersonTravelDayInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    personID: uuidSchema,
    travelDate: z.string().date(),
    remark: boundedNullableTextSchema(5000),
});
export const deleteProjectPersonTravelDayInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
});
const taskFields = {
    title: z.string().trim().min(1).max(1000),
    description: boundedNullableTextSchema(20000),
    state: z.enum(taskStates).optional(),
    dueDate: nullableDateSchema,
};
export const createTaskInputSchema = z.object({
    id: uuidSchema,
    ...taskFields,
});
export const updateTaskInputSchema = z
    .object({
    id: uuidSchema,
    title: taskFields.title.optional(),
    description: taskFields.description,
    state: taskFields.state,
    dueDate: taskFields.dueDate,
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one task field must be updated");
export const taskIDInputSchema = z.object({
    id: uuidSchema,
});
export const taskAssigneeInputSchema = z.object({
    id: uuidSchema,
    taskID: uuidSchema,
    personID: uuidSchema,
});
export const taskProjectInputSchema = z.object({
    id: uuidSchema,
    taskID: uuidSchema,
    projectID: uuidSchema,
});
export const taskProjectGroupInputSchema = z.object({
    id: uuidSchema,
    taskID: uuidSchema,
    projectGroupID: uuidSchema,
});
export const markNotificationReadInputSchema = z.object({
    id: uuidSchema,
});
export const markAllNotificationsReadInputSchema = z.object({});
export const toolFavoriteInputSchema = z.object({
    toolID: z
        .string()
        .max(64)
        .regex(/^[a-z0-9-]+$/),
});
export const createCommentInputSchema = z.object({
    id: uuidSchema,
    entityType: z.string().trim().min(1).max(100),
    entityID: uuidSchema,
    parentID: uuidSchema.nullable().optional(),
    content: z.string().trim().min(1).max(20000),
    mentionedPersonIDs: z.array(uuidSchema).optional(),
});
export const commentIDInputSchema = z.object({
    id: uuidSchema,
});
export const createStudioDocumentInputSchema = z.object({
    id: uuidSchema,
    documentID: uuidSchema,
    title: z.string().trim().min(1).max(1000),
    studioID: uuidSchema,
});
export const updateStudioDocumentInputSchema = z.object({
    id: uuidSchema,
    title: z.string().trim().min(1).max(1000),
});
export const studioDocumentIDInputSchema = z.object({
    id: uuidSchema,
});
const globalDocumentBucketSchema = z.enum(["team", "managers"]);
export const createGlobalDocumentInputSchema = z.object({
    id: uuidSchema,
    documentID: uuidSchema.nullable().optional(),
    title: z.string().trim().min(1).max(1000),
    parentID: uuidSchema.nullable().optional(),
    isFolder: z.boolean().optional(),
    hiddenFromDocs: z.boolean().optional(),
    bucket: globalDocumentBucketSchema.optional(),
});
export const updateGlobalDocumentInputSchema = z
    .object({
    id: uuidSchema,
    title: z.string().trim().min(1).max(1000).optional(),
    parentID: uuidSchema.nullable().optional(),
    sortOrder: z.number().int().optional(),
    bucket: globalDocumentBucketSchema.optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one global document field must be updated");
export const globalDocumentIDInputSchema = z.object({
    id: uuidSchema,
});
export const createGlobalDocumentLinkInputSchema = z.object({
    id: uuidSchema,
    globalDocumentID: uuidSchema,
    linkType: z.enum([
        "project",
        "project_group",
        "global_document",
        "project_note",
        "project_debriefing",
        "sim_cards",
    ]),
    linkID: uuidSchema,
});
export const deleteGlobalDocumentLinkInputSchema = z.object({
    id: uuidSchema,
    globalDocumentID: uuidSchema,
});
export const createMeetingNoteInputSchema = z.object({
    id: uuidSchema,
    documentID: uuidSchema,
    title: z.string().trim().min(1).max(1000),
    content: boundedNullableTextSchema(20000),
    meetingDate: z.string().date(),
    projectID: uuidSchema.nullable().optional(),
    projectGroupID: uuidSchema.nullable().optional(),
    tagIDs: z.array(uuidSchema).optional(),
    mentionedPersonIDs: z.array(uuidSchema).optional(),
    adminOnly: z.boolean().optional(),
    hiddenFromCollaborators: z.boolean().optional(),
});
export const updateMeetingNoteInputSchema = z
    .object({
    id: uuidSchema,
    title: z.string().trim().min(1).max(1000).optional(),
    content: boundedNullableTextSchema(20000),
    meetingDate: z.string().date().optional(),
    tagIDs: z.array(uuidSchema).optional(),
    mentionedPersonIDs: z.array(uuidSchema).optional(),
    adminOnly: z.boolean().optional(),
    hiddenFromCollaborators: z.boolean().optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one meeting note field must be updated");
export const meetingNoteIDInputSchema = z.object({
    id: uuidSchema,
});
export const createMeetingNoteTagInputSchema = z.object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(255),
    color: boundedNullableTextSchema(100),
});
export const updateMeetingNoteTagInputSchema = z
    .object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(255).optional(),
    color: boundedNullableTextSchema(100),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one meeting note tag field must be updated");
export const meetingNoteTagIDInputSchema = z.object({
    id: uuidSchema,
});
const templateTranslationsSchema = z
    .record(z.string().trim().min(1).max(10), z.string().trim().min(1).max(500))
    .refine((translations) => Object.keys(translations).length > 0, {
    message: "At least one template translation is required",
});
const quoteLineItemTemplateFields = {
    name: templateTranslationsSchema,
    code: boundedNullableTextSchema(100),
    defaultAmount: z.number().min(0).optional(),
    defaultUnitPrice: z.number().min(0).optional(),
    lineItemType: z.enum(lineItemTypes).optional(),
    category: boundedNullableTextSchema(255),
    sortOrder: z.number().int().optional(),
};
export const createQuoteLineItemTemplateInputSchema = z.object({
    id: uuidSchema,
    ...quoteLineItemTemplateFields,
});
export const updateQuoteLineItemTemplateInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(quoteLineItemTemplateFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one quote line item template field must be updated");
export const quoteLineItemTemplateIDInputSchema = z.object({
    id: uuidSchema,
});
export const renameQuoteLineItemTemplateCategoryInputSchema = z.object({
    oldCategory: z.string().trim().min(1).max(255),
    newCategory: z.string().trim().max(255),
});
const quoteGroupTemplateItemInputSchema = z.object({
    lineItemTemplateID: uuidSchema.nullable().optional(),
    defaultAmount: z.number().min(0).optional(),
    sortOrder: z.number().int().optional(),
    isTitle: z.boolean().optional(),
    name: templateTranslationsSchema.nullable().optional(),
});
const quoteGroupTemplateFields = {
    name: templateTranslationsSchema,
    defaultIsOptional: z.boolean().optional(),
    defaultApplyProjectDiscount: z.boolean().optional(),
    category: boundedNullableTextSchema(255),
    sortOrder: z.number().int().optional(),
};
export const createQuoteGroupTemplateInputSchema = z.object({
    id: uuidSchema,
    ...quoteGroupTemplateFields,
    items: z.array(quoteGroupTemplateItemInputSchema).optional(),
});
export const updateQuoteGroupTemplateInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(quoteGroupTemplateFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
    items: z.array(quoteGroupTemplateItemInputSchema).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one quote group template field must be updated");
export const quoteGroupTemplateIDInputSchema = z.object({
    id: uuidSchema,
});
export const renameQuoteGroupTemplateCategoryInputSchema = z.object({
    oldCategory: z.string().trim().min(1).max(255),
    newCategory: z.string().trim().max(255),
});
export const createQuoteInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(1000),
});
export const updateQuoteInputSchema = z
    .object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(1000).optional(),
    customerID: uuidSchema.nullable().optional(),
    contactID: uuidSchema.nullable().optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one quote field must be updated");
export const quoteIDInputSchema = z.object({
    id: uuidSchema,
});
export const createQuoteVersionInputSchema = z.object({
    id: uuidSchema,
    quoteID: uuidSchema,
    versionNumber: z.number().int().min(1),
    internalName: boundedNullableTextSchema(1000),
    quoteDate: z.string().date().optional(),
    validityDays: z.number().int().min(1).optional(),
    projectDiscountPercent: z.number().min(0).max(100).optional(),
    vatRate: z.enum(vatRates).optional(),
    generalConditions: boundedNullableTextSchema(50000),
});
export const updateQuoteVersionInputSchema = z
    .object({
    id: uuidSchema,
    internalName: boundedNullableTextSchema(1000),
    quoteDate: z.string().date().nullable().optional(),
    validUntil: z.string().date().optional(),
    validityDays: z.number().int().min(1).optional(),
    isAccepted: z.boolean().optional(),
    pdfURL: boundedNullableTextSchema(5000),
    projectDiscountPercent: z.number().min(0).max(100).optional(),
    vatRate: z.enum(vatRates).optional(),
    generalConditions: boundedNullableTextSchema(50000),
    status: z.enum(quoteVersionStatuses).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one quote version field must be updated");
export const quoteVersionIDInputSchema = z.object({
    id: uuidSchema,
});
export const setQuoteVersionStatusInputSchema = z.object({
    id: uuidSchema,
    status: z.enum(quoteVersionStatuses),
});
const quoteGroupFields = {
    quoteVersionID: uuidSchema,
    name: z.string().trim().max(1000).optional(),
    displayOrder: z.number().int().min(0).optional(),
    isOptional: z.boolean().optional(),
    applyProjectDiscount: z.boolean().optional(),
};
export const createQuoteGroupInputSchema = z.object({
    id: uuidSchema,
    ...quoteGroupFields,
});
export const updateQuoteGroupInputSchema = z
    .object({
    id: uuidSchema,
    name: z.string().trim().max(1000).optional(),
    displayOrder: z.number().int().min(0).optional(),
    isOptional: z.boolean().optional(),
    applyProjectDiscount: z.boolean().optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one quote group field must be updated");
export const quoteGroupIDInputSchema = z.object({
    id: uuidSchema,
});
const quoteLineItemFields = {
    quoteGroupID: uuidSchema,
    name: z.string().trim().max(1000).optional(),
    amount: z.number().optional(),
    factor: z.number().optional(),
    unitPrice: z.number().optional(),
    discount: z.number().min(0).max(100).optional(),
    notes: z.string().max(20000).optional(),
    externalNote: z.string().max(20000).optional(),
    displayOrder: z.number().int().min(0).optional(),
    isTitle: z.boolean().optional(),
    lineItemType: z.enum(lineItemTypes).optional(),
};
export const createQuoteLineItemInputSchema = z.object({
    id: uuidSchema,
    ...quoteLineItemFields,
});
export const updateQuoteLineItemInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(quoteLineItemFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one quote line item field must be updated");
export const quoteLineItemIDInputSchema = z.object({
    id: uuidSchema,
});
export const reorderQuoteLineItemsInputSchema = z.object({
    quoteGroupID: uuidSchema,
    orderedIDs: z.array(uuidSchema),
});
export const duplicateQuoteGroupInputSchema = z.object({
    id: uuidSchema,
    newID: uuidSchema,
    itemIDs: z.array(z.object({ sourceID: uuidSchema, newID: uuidSchema })),
});
export const duplicateQuoteVersionContentInputSchema = z.object({
    sourceVersionID: uuidSchema,
    targetVersionID: uuidSchema,
});
export const createQuoteDeductionItemInputSchema = z.object({
    id: uuidSchema,
    quoteVersionID: uuidSchema,
    description: z.string().trim().max(1000).optional(),
    amount: z.number().optional(),
    displayOrder: z.number().int().min(0).optional(),
});
export const updateQuoteDeductionItemInputSchema = z
    .object({
    id: uuidSchema,
    description: z.string().trim().max(1000).optional(),
    amount: z.number().optional(),
    displayOrder: z.number().int().min(0).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one quote deduction item field must be updated");
export const quoteDeductionItemIDInputSchema = z.object({
    id: uuidSchema,
});
export const createInvoiceInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema.nullable().optional(),
    title: z.string().trim().min(1).max(1000),
    language: z.enum(languages).nullable().optional(),
    currency: z.enum(invoiceCurrencies).optional(),
    sourceQuoteVersionID: uuidSchema.nullable().optional(),
    customerID: uuidSchema.nullable().optional(),
    contactID: uuidSchema.nullable().optional(),
    depositPercent: z.number().min(0).max(100).nullable().optional(),
    projectDiscountPercent: z.number().min(0).max(100).optional(),
    vatRate: z.enum(vatRates).optional(),
    includeQRBill: z.boolean().optional(),
});
export const updateInvoiceInputSchema = z
    .object({
    id: uuidSchema,
    title: z.string().trim().min(1).max(1000).optional(),
    language: z.enum(languages).nullable().optional(),
    currency: z.enum(invoiceCurrencies).optional(),
    invoiceDate: z.string().date().nullable().optional(),
    dueDate: z.string().date().nullable().optional(),
    paymentTermDays: z.number().int().min(0).optional(),
    projectDiscountPercent: z.number().min(0).max(100).optional(),
    vatRate: z.enum(vatRates).optional(),
    depositPercent: z.number().min(0).max(100).nullable().optional(),
    includeQRBill: z.boolean().optional(),
    pdfURL: boundedNullableTextSchema(5000),
    notes: boundedNullableTextSchema(20000),
    totalNet: z.number().optional(),
    totalGross: z.number().optional(),
    qrReference: boundedNullableTextSchema(255),
    customerID: uuidSchema.nullable().optional(),
    contactID: uuidSchema.nullable().optional(),
    status: z.enum(invoiceStatuses).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one invoice field must be updated");
export const invoiceIDInputSchema = z.object({
    id: uuidSchema,
});
export const transitionInvoiceStatusInputSchema = z.object({
    id: uuidSchema,
    status: z.enum(invoiceStatuses),
});
export const invoiceReminderStatuses = ["sent", "paid", "cancelled"];
export const createInvoiceReminderInputSchema = z.object({
    id: uuidSchema,
    invoiceID: uuidSchema,
    title: z.string().trim().min(1).max(1000),
    reminderDate: z.string().date(),
    dueDate: z.string().date(),
    paymentTermDays: z.number().int().min(0),
    pdfURL: boundedNullableTextSchema(5000),
    totalNet: z.number(),
    totalGross: z.number(),
});
export const updateInvoiceReminderInputSchema = z
    .object({
    id: uuidSchema,
    title: z.string().trim().min(1).max(1000).optional(),
    reminderDate: z.string().date().optional(),
    dueDate: z.string().date().optional(),
    paymentTermDays: z.number().int().min(0).optional(),
    pdfURL: boundedNullableTextSchema(5000),
    status: z.enum(invoiceReminderStatuses).optional(),
    totalNet: z.number().optional(),
    totalGross: z.number().optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one invoice reminder field must be updated");
export const invoiceReminderIDInputSchema = z.object({
    id: uuidSchema,
});
const invoiceGroupFields = {
    invoiceID: uuidSchema,
    name: z.string().trim().max(1000).optional(),
    displayOrder: z.number().int().min(0).optional(),
    isOptional: z.boolean().optional(),
    applyProjectDiscount: z.boolean().optional(),
};
export const createInvoiceGroupInputSchema = z.object({
    id: uuidSchema,
    ...invoiceGroupFields,
});
export const updateInvoiceGroupInputSchema = z
    .object({
    id: uuidSchema,
    name: z.string().trim().max(1000).optional(),
    displayOrder: z.number().int().min(0).optional(),
    isOptional: z.boolean().optional(),
    applyProjectDiscount: z.boolean().optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one invoice group field must be updated");
export const invoiceGroupIDInputSchema = z.object({
    id: uuidSchema,
});
const invoiceLineItemFields = {
    invoiceGroupID: uuidSchema,
    name: z.string().trim().max(1000).optional(),
    amount: z.number().optional(),
    factor: z.number().optional(),
    unitPrice: z.number().optional(),
    discount: z.number().min(0).max(100).optional(),
    notes: z.string().max(20000).optional(),
    externalNote: z.string().max(20000).optional(),
    displayOrder: z.number().int().min(0).optional(),
    isTitle: z.boolean().optional(),
    lineItemType: z.enum(lineItemTypes).optional(),
};
export const createInvoiceLineItemInputSchema = z.object({
    id: uuidSchema,
    ...invoiceLineItemFields,
});
export const updateInvoiceLineItemInputSchema = z
    .object({
    id: uuidSchema,
    ...Object.fromEntries(Object.entries(invoiceLineItemFields).map(([key, schema]) => [
        key,
        schema.optional(),
    ])),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one invoice line item field must be updated");
export const invoiceLineItemIDInputSchema = z.object({
    id: uuidSchema,
});
export const reorderInvoiceLineItemsInputSchema = z.object({
    invoiceGroupID: uuidSchema,
    orderedIDs: z.array(uuidSchema),
});
export const duplicateInvoiceGroupInputSchema = z.object({
    id: uuidSchema,
    newID: uuidSchema,
    itemIDs: z.array(z.object({ sourceID: uuidSchema, newID: uuidSchema })),
});
export const createInvoiceDeductionItemInputSchema = z.object({
    id: uuidSchema,
    invoiceID: uuidSchema,
    description: z.string().trim().max(1000).optional(),
    amount: z.number().optional(),
    displayOrder: z.number().int().min(0).optional(),
    sourceInvoiceID: uuidSchema.nullable().optional(),
});
export const updateInvoiceDeductionItemInputSchema = z
    .object({
    id: uuidSchema,
    description: z.string().trim().max(1000).optional(),
    amount: z.number().optional(),
    displayOrder: z.number().int().min(0).optional(),
    sourceInvoiceID: uuidSchema.nullable().optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one invoice deduction item field must be updated");
export const invoiceDeductionItemIDInputSchema = z.object({
    id: uuidSchema,
});
export const copyQuoteContentToInvoiceInputSchema = z.object({
    sourceQuoteVersionID: uuidSchema,
    targetInvoiceID: uuidSchema,
});
export const createIntercomChannelInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(500),
    description: boundedNullableTextSchema(2000),
    channelNumber: z.number().int().min(1).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
});
export const updateIntercomChannelInputSchema = z
    .object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(500).optional(),
    description: boundedNullableTextSchema(2000),
    channelNumber: z.number().int().min(1).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one intercom channel field must be updated");
export const intercomChannelIDInputSchema = z.object({
    id: uuidSchema,
});
export const createIntercomAssignmentInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    personID: nullableUUIDSchema,
    profileID: nullableUUIDSchema,
    unityUsername: boundedNullableTextSchema(255),
    displayName: boundedNullableTextSchema(500),
});
export const updateIntercomAssignmentInputSchema = z
    .object({
    id: uuidSchema,
    profileID: nullableUUIDSchema,
    unityUsername: z.string().trim().min(1).max(255).optional(),
    displayName: boundedNullableTextSchema(500),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one intercom assignment field must be updated");
export const intercomAssignmentIDInputSchema = z.object({
    id: uuidSchema,
});
export const createIntercomProfileInputSchema = z.object({
    id: uuidSchema,
    projectID: uuidSchema,
    name: z.string().trim().min(1).max(255),
    channelConfig: z.record(z.object({ talk: z.boolean(), listen: z.boolean() })),
});
export const updateIntercomProfileInputSchema = z
    .object({
    id: uuidSchema,
    name: z.string().trim().min(1).max(255).optional(),
    channelConfig: z
        .record(z.object({ talk: z.boolean(), listen: z.boolean() }))
        .optional(),
})
    .refine(({ id: _id, ...updates }) => Object.values(updates).some((value) => value !== undefined), "At least one intercom profile field must be updated");
export const intercomProfileIDInputSchema = z.object({
    id: uuidSchema,
});
export const bulkAssignIntercomProfileInputSchema = z.object({
    profileID: nullableUUIDSchema,
    assignmentIDs: z.array(uuidSchema).min(1),
});
// Query args. These mirror what the web app sends today, so IDs stay plain
// strings: some call sites pass placeholders ("", "__none__", the zero UUID)
// while a lookup is not ready yet.
export const idQueryArgsSchema = z.object({
    id: z.string(),
});
export const personByIDQueryArgsSchema = z.object({
    id: z.string().describe("Person ID"),
});
export const projectIDQueryArgsSchema = z.object({
    projectID: z.string(),
});
export const projectGroupIDQueryArgsSchema = z.object({
    projectGroupID: z.string(),
});
export const personIDQueryArgsSchema = z.object({
    personID: z.string(),
});
export const personIDsQueryArgsSchema = z.object({
    personIDs: z.array(z.string()),
});
export const projectsByIDsQueryArgsSchema = z.object({
    ids: z.array(z.string()).max(100),
});
export const myTasksQueryArgsSchema = z.object({
    personID: z
        .string()
        .describe("The caller's own person ID; other IDs return nothing"),
});
export const myUpcomingProjectsQueryArgsSchema = z.object({
    personID: z
        .string()
        .describe("The caller's own person ID; other IDs return nothing"),
});
export const includeDeletedQueryArgsSchema = z.object({
    includeDeleted: z.boolean().optional().describe("Include soft-deleted rows"),
});
export const studioIDQueryArgsSchema = z.object({
    studioID: z.string(),
});
export const playoutRunLogsByProjectQueryArgsSchema = z.object({
    projectID: z.string(),
    limit: z.number().int().min(1).max(5000).optional(),
});
export const playoutRunLogsByConfigQueryArgsSchema = z.object({
    configID: z.string(),
    limit: z.number().int().min(1).max(5000).optional(),
});
export const studioConfigLoadsQueryArgsSchema = z.object({
    studioID: z.string(),
    limit: z.number().int().min(1).max(200).optional(),
});
export const studioWiringChangesQueryArgsSchema = z.object({
    studioID: z.string(),
    /** Newest audit rows to return (default 500) */
    limit: z.number().int().min(1).max(5000).optional(),
});
export const studioConfigIDQueryArgsSchema = z.object({
    configID: z.string().describe("Studio config ID"),
});
export const quoteVersionIDQueryArgsSchema = z.object({
    quoteVersionID: z.string(),
});
export const invoiceIDQueryArgsSchema = z.object({
    invoiceID: z.string(),
});
export const appSettingByKeyQueryArgsSchema = z.object({
    key: z.string().describe("Setting key"),
});
export const commentsByEntityQueryArgsSchema = z.object({
    entityType: z
        .string()
        .describe("Kind of entity the comments belong to, e.g. project, project_group, company, equipment, studio, meeting_note"),
    entityID: z.string().describe("ID of the commented entity"),
});
export const globalDocumentLinksByEntityQueryArgsSchema = z.object({
    linkType: z
        .string()
        .describe("Kind of linked entity: project, project_group, global_document, project_note, project_debriefing or sim_cards"),
    linkID: z.string().describe("ID of the linked entity"),
});
export const globalDocumentIDQueryArgsSchema = z.object({
    documentID: z.string().describe("Global document (Docs tree entry) ID"),
});
export const yearQueryArgsSchema = z.object({
    year: z
        .number()
        .optional()
        .describe("Only rows within this calendar year (e.g. 2026); omit for all"),
});
export const personYearQueryArgsSchema = z.object({
    personID: z.string(),
    year: z
        .number()
        .optional()
        .describe("Only rows within this calendar year (e.g. 2026); omit for all"),
});
export const skillRatingsAtLeastQueryArgsSchema = z.object({
    minRating: z.number().describe("Minimum skill rating (inclusive)"),
});
